/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { ButtonMore, Typography } from "./..";
import OptionWrapper from "../others/option-wrapper";
import { Icon } from "../../icons/effect";
import PropTypes from 'prop-types';
import React, { forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react';
import { hexToRGBA } from "../../styles/color-helper";
import { alphaPseudo, bgColor, borderRadius, cursorPointer, displayFlex, flexWrap, gap, itemsCenter, listNone, mg, pd, pdl, pdr, textColor, userSelectNone } from "../../styles/general";
import { useTypography as typography } from "../../theme";
import { classNames } from "../../utils";
const sizeIconMap = new Map([['tiny', 16], ['small', 18], ['medium', 20]]);
const sizeTypographyPMap = new Map([['tiny', 'p3'], ['small', 'p2'], ['medium', 'p1']]);
const sizeTypographyHMap = new Map([['tiny', 'h5'], ['small', 'h4'], ['medium', 'h3']]);
const Breadcrumb = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  children,
  className,
  color,
  data,
  id,
  itemsAfterCollapse,
  itemsBeforeCollapse,
  maxItems,
  separator,
  size,
  style
}, reference) => {
  if (!reference) reference = useRef(null);
  if (color === 'default') color = 'system/rest';
  const ref = useRef(null);
  const sizeIcon = sizeIconMap.get(size);
  const sizeTypographyP = sizeTypographyPMap.get(size);
  const sizeTypographyH = sizeTypographyHMap.get(size);

  // const [expanded, setExpanded] = React.useState(false);

  const _BreadcrumbLiCSS = BreadcrumbLiCSS(color, sizeTypographyP, sizeTypographyH);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  const renderSeparator = typeof separator === 'string' ? jsx(Icon, {
    name: separator,
    width: sizeIcon,
    height: sizeIcon,
    color: color,
    viewBox: true
  }) : separator;
  const renderItemsBeforeAndAfter = allItems => {
    // const handleClickExpand = () => {
    //     setExpanded(true);
    // };

    if (itemsBeforeCollapse + itemsAfterCollapse >= allItems.length) {
      return allItems;
    }
    let options = [];
    data.slice(itemsBeforeCollapse, allItems.length - itemsAfterCollapse).map(item => {
      var _item$onClick;
      return options = [...options, {
        label: (item === null || item === void 0 ? void 0 : item.label) || (item === null || item === void 0 ? void 0 : item.name),
        icon: item === null || item === void 0 ? void 0 : item.icon,
        action: item === null || item === void 0 ? void 0 : (_item$onClick = item.onClick) === null || _item$onClick === void 0 ? void 0 : _item$onClick.call(item)
      }];
    });
    return [...allItems.slice(0, itemsBeforeCollapse), jsx(ButtonMore, {
      key: "ellipsis",
      options: options,
      anchor: jsx("li", {
        key: "ellipsis",
        css: _BreadcrumbLiCSS
      }, jsx(Icon, {
        name: 'MoreHoriz',
        width: sizeIcon,
        height: sizeIcon,
        color: color
      }), renderSeparator)
    }),
    // <ButtonIcon key="ellipsis" name={'MoreHoriz'} width={24} height={16} onClick={handleClickExpand} />,
    ...allItems.slice(allItems.length - itemsAfterCollapse, allItems.length)];
  };
  const allItems = data !== null && data !== void 0 && data.length ? data.map((item, idx) => jsx("li", {
    css: _BreadcrumbLiCSS,
    className: 'DGN-UI-Breadcrumb-li',
    key: `child-${idx}`
  }, item !== null && item !== void 0 && item.icon ? jsx(Icon, {
    className: 'DGN-UI-Breadcrumb-li-icon',
    name: item === null || item === void 0 ? void 0 : item.icon,
    color: color,
    width: sizeIcon,
    height: sizeIcon
  }) : null, jsx(Typography, {
    className: 'DGN-UI-Breadcrumb-li-label',
    type: (data === null || data === void 0 ? void 0 : data.length) === idx + 1 ? sizeTypographyH : sizeTypographyP,
    mapping: 'a',
    color: 'currentColor',
    href: item === null || item === void 0 ? void 0 : item.href,
    onClick: item === null || item === void 0 ? void 0 : item.onClick
  }, (item === null || item === void 0 ? void 0 : item.label) || (item === null || item === void 0 ? void 0 : item.name)), (data === null || data === void 0 ? void 0 : data.length) !== idx + 1 ? renderSeparator : null)) : React.Children.toArray(children).filter(child => {
    return /*#__PURE__*/React.isValidElement(child);
  }).map((child, idx) => {
    var _React$Children$toArr;
    return jsx("li", {
      css: _BreadcrumbLiCSS,
      className: 'DGN-UI-Breadcrumb-li',
      key: `child-${idx}`
    }, child, ((_React$Children$toArr = React.Children.toArray(children).filter(child => {
      return /*#__PURE__*/React.isValidElement(child);
    })) === null || _React$Children$toArr === void 0 ? void 0 : _React$Children$toArr.length) !== idx + 1 ? renderSeparator : null);
  });
  return useMemo(() => {
    return jsx("nav", {
      ref: ref,
      id: id,
      style: style,
      className: classNames('DGN-UI-Breadcrumb', className)
    }, jsx("ol", {
      css: BreadcrumbOlCSS(color),
      className: 'DGN-UI-Breadcrumb-ol'
    },
    // expanded ||
    maxItems && allItems.length <= maxItems ? allItems : renderItemsBeforeAndAfter(allItems)));
  }, [children, className, color, data, id, itemsAfterCollapse, itemsBeforeCollapse, maxItems, separator, size, style
  // expanded,
  ]);
}));

const BreadcrumbOlCSS = color => css`
    ${displayFlex};
    ${flexWrap};
    ${itemsCenter};
    ${pd(0)};
    ${mg(0)};
    ${listNone};
    ${textColor(color)};
`;
const BreadcrumbLiCSS = (color, sizeTypographyP, sizeTypographyH) => css`
    ${typography[sizeTypographyP]};
    ${displayFlex};
    ${itemsCenter};
    ${gap('4px')};
    ${pdl([1])};
    ${borderRadius(4)};
    ${cursorPointer};
    ${userSelectNone};
    &:last-child {
        ${pdr([1])};
        ${typography[sizeTypographyH]};
    }
    ${Object.keys(alphaPseudo).map(key => css`
                &: ${key} {
                    ${bgColor(hexToRGBA(color, alphaPseudo[key]))};
                }
            `)}
`;
Breadcrumb.defaultProps = {
  className: '',
  color: 'default',
  itemsAfterCollapse: 1,
  itemsBeforeCollapse: 1,
  maxItems: 8,
  separator: 'ArrowRight',
  size: 'medium',
  style: {}
};
Breadcrumb.propTypes = {
  /** The content of the component. */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Class for component. */
  className: PropTypes.string,
  /** The color of the component. */
  color: PropTypes.string,
  /** Class for component. */
  data: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    icon: PropTypes.string,
    href: PropTypes.string,
    onClick: PropTypes.func
  })),
  /** Class for component. */
  itemsBeforeCollapse: PropTypes.number,
  /** Class for component. */
  itemsAfterCollapse: PropTypes.number,
  /** Class for component. */
  maxItems: PropTypes.number,
  /**	Custom separator node. */
  separator: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** The size of the component. */
  size: PropTypes.oneOf(['tiny', 'small', 'medium']),
  /** Style inline of component. */
  style: PropTypes.object
};
export { Breadcrumb };
export default OptionWrapper(Breadcrumb);